/**
 * Библиотека проекта тремя кучами: разложенные по слотам, не разобранные и
 * битые ссылки CRM (с HTTP-кодом — по нему видно, «нет файла» или «не пустили»).
 * Импорт из CRM синхронный: файлы скачиваются к нам, ссылка CRM — только история.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { useState } from "react";

import { mediaUrl } from "@/lib/media";
import type { LibAsset, ProjectLibrary as Library, ProjectView, Slot } from "@/lib/project";
import { errorText, postJson, qk } from "@/lib/project";

import { Btn, Empty, ErrorLine, Hint, Panel, selectClass } from "./ui";

export function ProjectLibrary({ pid, library, slots }: { pid: number; library: Library; slots: Slot[] }) {
  const client = useQueryClient();
  const [error, setError] = useState("");

  const assign = useMutation({
    mutationFn: ({ id, slot }: { id: number; slot: string }) =>
      postJson(`/api/project-assets/${id}/slot`, { slot_code: slot }),
    onMutate: () => setError(""),
    // Слот ассета меняет и раскладку библиотеки, и то, что кадры наследуют, —
    // проще и честнее перечитать проект, чем раскладывать руками.
    onSuccess: () => client.invalidateQueries({ queryKey: qk.project(pid) }),
    onError: (e) => setError(errorText(e)),
  });

  const importCrm = useMutation({
    mutationFn: () => postJson<{ library: Library }>(`/api/projects/${pid}/import`, {}),
    onMutate: () => setError(""),
    onSuccess: (result) => {
      client.setQueryData<ProjectView>(qk.project(pid), (old) => (old ? { ...old, library: result.library } : old));
      void client.invalidateQueries({ queryKey: qk.project(pid) });
    },
    onError: (e) => setError(errorText(e)),
  });

  const empty = !library.assigned.length && !library.loose.length && !library.broken.length;

  const tile = (a: LibAsset, assigned: boolean) => (
    <div key={a.id} data-annot={`ассет ${a.id}`} className="grid gap-1">
      <a href={mediaUrl(a.asset_path)} target="_blank" rel="noopener" title={a.note ?? undefined}>
        <img
          src={mediaUrl(a.asset_path)}
          alt={a.slot_name ?? "без слота"}
          loading="lazy"
          className="aspect-[3/4] w-full rounded-[4px] border border-border object-cover"
        />
      </a>
      <select
        value={a.slot_code ?? ""}
        disabled={assign.isPending && assign.variables?.id === a.id}
        onChange={(e) => assign.mutate({ id: a.id, slot: e.target.value })}
        className={`${selectClass} h-7 w-full px-1 text-[11.5px]`}
      >
        <option value="">{assigned ? "— снять слот —" : "— назначить слот —"}</option>
        {slots.map((s) => (
          <option key={s.slot_code} value={s.slot_code}>
            {s.slot_name}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <Panel title="Библиотека" annot="библиотека проекта">
      <div className="grid gap-4">
        {library.assigned.length ? (
          <div>
            <Hint>разложено по слотам — {library.assigned.length}</Hint>
            <div className="mt-1.5 grid grid-cols-[repeat(auto-fill,minmax(88px,1fr))] gap-2">
              {library.assigned.map((a) => tile(a, true))}
            </div>
          </div>
        ) : null}

        {library.loose.length ? (
          <div>
            <Hint>без слота — {library.loose.length}</Hint>
            <div className="mt-1.5 grid grid-cols-[repeat(auto-fill,minmax(88px,1fr))] gap-2">
              {library.loose.map((a) => tile(a, false))}
            </div>
          </div>
        ) : null}

        {library.broken.length ? (
          <div>
            <Hint>битые ссылки CRM — {library.broken.length}</Hint>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {library.broken.map((a) => (
                <a
                  key={a.id}
                  href={a.url ?? undefined}
                  target="_blank"
                  rel="noopener"
                  title={a.url ?? ""}
                  className="tnum rounded-full bg-err-bg px-2 py-0.5 text-[11px] text-err hover:underline"
                >
                  HTTP {a.http ?? "—"}
                </a>
              ))}
            </div>
          </div>
        ) : null}

        {empty ? <Empty>Библиотека пуста.</Empty> : null}

        <ErrorLine text={error} />

        <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
          <Btn disabled={importCrm.isPending} onClick={() => importCrm.mutate()}>
            <Download size={13} />
            {importCrm.isPending ? "импортирую…" : "Импорт фото из CRM"}
          </Btn>
          <Hint>файлы скачиваются к нам, ссылка CRM — только история</Hint>
        </div>
      </div>
    </Panel>
  );
}
